import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    McButton,
    McCard,
    McIcon,
    McDrawer,
    McInput,
    McList,
    McListItem
} from '@maersk-global/mds-react-wrapper/components-core';
import {
    evictAllCaches,
    evictCache,
    evictCacheKey,
    warmUpCache,
    CacheStatistics,
    gatherClusterStats,
    resetClusterStats,
    getCacheKeys
} from '../services/cacheApi';
import {
    agentService,
    ConversationSessionEntry,
    ConversationSettings,
} from '../services/agentService';
import { useNotification } from '../context/NotificationContext';
import { ConfirmationDialog } from '../components/Common/ConfirmationDialog';
import { useAuth } from '../context/AuthContext';
import { roleConfig } from '../config/roles';

export default function CacheManagementPage() {
    const navigate = useNavigate();
    const { hasRole } = useAuth();
    const canManageUserQuota = hasRole(roleConfig.manageUserQuota);
    const queryClient = useQueryClient();
    const { showNotification } = useNotification();
    const [activeTab, setActiveTab] = useState<'caches' | 'sessions'>('caches');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCache, setSelectedCache] = useState<string | null>(null);
    const [confirmAllOpen, setConfirmAllOpen] = useState(false);
    const [confirmGatherOpen, setConfirmGatherOpen] = useState(false);
    const [confirmResetOpen, setConfirmResetOpen] = useState(false);
    const [statsData, setStatsData] = useState<Record<string, CacheStatistics[]> | null>(null);

    // Evict all mutation
    const evictAllMutation = useMutation({
        mutationFn: evictAllCaches,
        onSuccess: () => {
            showNotification('All caches evicted successfully', 'success');
            if (statsData) gatherMutation.mutate();
            setConfirmAllOpen(false);
        },
        onError: (error: any) => {
            showNotification(`Failed to evict caches: ${error.message}`, 'error');
            setConfirmAllOpen(false);
        }
    });

    // Gather stats mutation
    const gatherMutation = useMutation({
        mutationFn: gatherClusterStats,
        onSuccess: (data) => {
            setStatsData(data);
            showNotification('Cluster statistics gathered successfully', 'success');
            setConfirmGatherOpen(false);
        },
        onError: (error: any) => {
            showNotification(`Failed to gather statistics: ${error.message}`, 'error');
            setConfirmGatherOpen(false);
        }
    });

    // Reset stats mutation
    const resetStatsMutation = useMutation({
        mutationFn: resetClusterStats,
        onSuccess: () => {
            showNotification('Cluster statistics reset successfully', 'success');
            setStatsData(null);
            setConfirmResetOpen(false);
        },
        onError: (error: any) => {
            showNotification(`Failed to reset statistics: ${error.message}`, 'error');
            setConfirmResetOpen(false);
        }
    });

    const warmupMutation = useMutation({
        mutationFn: warmUpCache,
        onSuccess: () => {
            showNotification('Cache warmup started successfully', 'success');
            setTimeout(() => {
                if (statsData) gatherMutation.mutate();
            }, 2000);
        },
        onError: (error: any) => {
            showNotification(`Failed to warmup cache: ${error.message}`, 'error');
        }
    });

    const handleExportJson = () => {
        if (!statsData) return;
        const blob = new Blob([JSON.stringify(statsData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cache-stats-${new Date().toISOString()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* ── Page Header ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ margin: 0 }}>Redis Cache Management</h2>
                    <p style={{ margin: '8px 0 0', color: 'var(--mds-color-neutral-text-secondary)' }}>
                        Admin-only view for monitoring and managing cluster-wide application caches and conversation sessions.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                    {canManageUserQuota && (
                        <McButton
                            appearance="secondary"
                            variant="outlined"
                            onClick={() => navigate('/admin/user-quotas')}
                        >
                            <McIcon icon="lock" slot="prefix" />
                            User Quotas
                        </McButton>
                    )}
                </div>
            </div>

            {/* ── Tab Bar ── */}
            <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--mds-color-neutral-weak-decoration)' }}>
                {([
                    { key: 'caches' as const, label: 'Cache Statistics', icon: 'bar-chart' },
                    { key: 'sessions' as const, label: 'Conversation Sessions', icon: 'chat' },
                ]).map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 24px',
                            border: 'none',
                            borderBottom: activeTab === tab.key
                                ? '3px solid var(--mds-color-primary-60, #0073ab)'
                                : '3px solid transparent',
                            marginBottom: '-2px',
                            background: 'none',
                            cursor: 'pointer',
                            fontWeight: activeTab === tab.key ? 700 : 400,
                            color: activeTab === tab.key
                                ? 'var(--mds-color-primary-60, #0073ab)'
                                : 'var(--mds-color-neutral-text-secondary)',
                            fontSize: '14px',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        <McIcon icon={tab.icon} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Tab Content ── */}
            {activeTab === 'caches' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Cache toolbar */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <McButton
                            appearance="secondary"
                            variant="outlined"
                            onClick={() => warmupMutation.mutate()}
                            disabled={warmupMutation.isPending}
                        >
                            <McIcon icon="lightning" slot="prefix" />
                            Warm Up Caches
                        </McButton>
                        <McButton
                            appearance="primary"
                            variant="filled"
                            onClick={() => setConfirmAllOpen(true)}
                            disabled={evictAllMutation.isPending}
                        >
                            <McIcon icon="trash" slot="prefix" />
                            Evict All
                        </McButton>
                    </div>

                    {!statsData ? (
                        <McCard style={{ padding: '48px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                            <h3>Cluster Statistics Ready</h3>
                            <p style={{ color: 'var(--mds-color-neutral-text-secondary)', maxWidth: '500px' }}>
                                Gathering statistics will pulse all pods in the cluster to report their current hit/miss counters and memory usage. This provides a total system view.
                            </p>
                            <McButton
                                appearance="primary"
                                onClick={() => setConfirmGatherOpen(true)}
                                disabled={gatherMutation.isPending}
                                style={{ marginTop: '16px' }}
                            >
                                {gatherMutation.isPending ? 'Gathering...' : 'Gather Cluster Statistics'}
                            </McButton>
                        </McCard>
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--mds-color-neutral-weak-canvas)', padding: '16px', borderRadius: '8px' }}>
                                <span style={{ fontWeight: 'bold' }}>Statistics last updated: {new Date().toLocaleTimeString()}</span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <McButton appearance="secondary" variant="outlined" onClick={handleExportJson}>
                                        <McIcon icon="tray-arrow-down" slot="prefix" /> Export JSON
                                    </McButton>
                                    <McButton appearance="secondary" variant="outlined" onClick={() => gatherMutation.mutate()} disabled={gatherMutation.isPending}>
                                        <McIcon icon="rotate" slot="prefix" /> Refresh
                                    </McButton>
                                    <McButton appearance="error" variant="outlined" onClick={() => setConfirmResetOpen(true)} disabled={resetStatsMutation.isPending}>
                                        Reset Stats
                                    </McButton>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '24px' }}>
                                {Object.entries(statsData).map(([cacheName, podStats]) => (
                                    <CacheClusterCard
                                        key={cacheName}
                                        cacheName={cacheName}
                                        podStats={podStats}
                                        onRefresh={() => gatherMutation.mutate()}
                                        onViewKeys={(name) => {
                                            setSelectedCache(name);
                                            setDrawerOpen(true);
                                        }}
                                    />
                                ))}
                                {Object.keys(statsData).length === 0 && (
                                    <McCard style={{ padding: '24px', gridColumn: '1 / -1', textAlign: 'center' }}>
                                        No caches reported metrics.
                                    </McCard>
                                )}
                            </div>
                        </>
                    )}

                    {selectedCache && (
                        <CacheKeysDrawer
                            cacheName={selectedCache}
                            isOpen={drawerOpen}
                            onClose={() => {
                                setDrawerOpen(false);
                                setTimeout(() => setSelectedCache(null), 300);
                            }}
                        />
                    )}
                </div>
            )}

            {activeTab === 'sessions' && (
                <ConversationSessionsSection />
            )}

            {/* ── Shared Confirmation Dialogs ── */}
            <ConfirmationDialog
                open={confirmGatherOpen}
                onClose={() => setConfirmGatherOpen(false)}
                onConfirm={() => gatherMutation.mutate()}
                title="Gather Cluster Statistics"
                content={
                    <div>
                        <p>This will request real-time statistics from all pods in the system.</p>
                        <p style={{ marginTop: '8px' }}>Do you want to proceed?</p>
                    </div>
                }
                confirmLabel="Gather Stats"
                variant="primary"
                loading={gatherMutation.isPending}
            />

            <ConfirmationDialog
                open={confirmResetOpen}
                onClose={() => setConfirmResetOpen(false)}
                onConfirm={() => resetStatsMutation.mutate()}
                title="Reset Cluster Statistics"
                content={
                    <div>
                        <p>Are you sure you want to reset cache performance counters to zero?</p>
                        <p style={{ color: 'var(--mds-color-error-60)', marginTop: '8px' }}>
                            <strong>Warning:</strong> This will publish a reset signal to all pods in the cluster, clearing their historical hit/miss tracking.
                        </p>
                    </div>
                }
                confirmLabel="Reset Stats"
                variant="danger"
                loading={resetStatsMutation.isPending}
            />

            <ConfirmationDialog
                open={confirmAllOpen}
                onClose={() => setConfirmAllOpen(false)}
                onConfirm={() => evictAllMutation.mutate()}
                title="Evict All Caches"
                content={
                    <div>
                        <p>Are you sure you want to evict all caches across the entire system?</p>
                        <p style={{ color: 'var(--mds-color-error-60)', marginTop: '8px' }}>
                            <strong>Warning:</strong> This action is global and will affect all instances. It may cause temporary performance degradation while caches are repopulated.
                        </p>
                    </div>
                }
                confirmLabel="Evict All"
                variant="danger"
                loading={evictAllMutation.isPending}
            />

        </div>
    );
}

function ConversationSessionsSection() {
    const { showNotification } = useNotification();
    const [userFilter, setUserFilter] = useState('');
    const [sessions, setSessions] = useState<ConversationSessionEntry[]>([]);
    const [confirmEvictAllOpen, setConfirmEvictAllOpen] = useState(false);
    const [confirmEvictUserOpen, setConfirmEvictUserOpen] = useState<string | null>(null);
    const [selectedSession, setSelectedSession] = useState<ConversationSessionEntry | null>(null);
    const [sessionMessages, setSessionMessages] = useState<Array<{ role: string; content: string }>>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);

    // ── Settings state ──
    const [settings, setSettings] = useState<ConversationSettings | null>(null);
    const [editTtl, setEditTtl] = useState('');
    const [editMaxTurns, setEditMaxTurns] = useState('');
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    // Load settings on mount
    const settingsQuery = useMutation({
        mutationFn: () => agentService.getConversationSettings(),
        onSuccess: (data) => {
            setSettings(data);
            setEditTtl(String(data.ttlSeconds));
            setEditMaxTurns(String(data.maxTurns));
        },
        onError: (error: any) => showNotification(`Failed to load settings: ${error?.message}`, 'error'),
    });

    // Save settings
    const saveSettingsMutation = useMutation({
        mutationFn: (payload: Partial<ConversationSettings>) =>
            agentService.updateConversationSettings(payload),
        onSuccess: (data) => {
            setSettings(data);
            setEditTtl(String(data.ttlSeconds));
            setEditMaxTurns(String(data.maxTurns));
            showNotification('Conversation settings saved', 'success');
        },
        onError: (error: any) => showNotification(`Failed to save settings: ${error?.message}`, 'error'),
    });

    // Load settings on first expand
    const handleToggleSettings = () => {
        if (!settingsExpanded && !settings) settingsQuery.mutate();
        setSettingsExpanded(!settingsExpanded);
    };

    const handleSaveSettings = () => {
        const payload: Partial<ConversationSettings> = {};
        const ttlNum = parseInt(editTtl, 10);
        const maxNum = parseInt(editMaxTurns, 10);
        if (!isNaN(ttlNum) && ttlNum >= 60) payload.ttlSeconds = ttlNum;
        if (!isNaN(maxNum) && maxNum >= 2) payload.maxTurns = maxNum;
        if (Object.keys(payload).length > 0) saveSettingsMutation.mutate(payload);
    };

    const formatTtlInput = (seconds: number) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    const loadMutation = useMutation({
        mutationFn: (params: { user: string; limit: number }) =>
            agentService.listConversationSessions(params.user, params.limit),
        onSuccess: (data) => { setSessions(data.sessions ?? []); setLoaded(true); },
        onError: (error: any) => showNotification(`Failed to load sessions: ${error?.message}`, 'error'),
    });

    const evictOneMutation = useMutation({
        mutationFn: (s: ConversationSessionEntry) =>
            agentService.evictConversationSession(s.userId, s.agentId, s.sessionId),
        onSuccess: (_, s) => {
            showNotification(`Session evicted`, 'success');
            setSessions((prev) => prev.filter((x) => x.key !== s.key));
        },
        onError: (error: any) => showNotification(`Evict failed: ${error?.message}`, 'error'),
    });

    const evictUserMutation = useMutation({
        mutationFn: (userId: string) => agentService.evictUserConversationSessions(userId),
        onSuccess: (data, userId) => {
            showNotification(`${data.sessionsEvicted} sessions evicted for ${userId}`, 'success');
            setSessions((prev) => prev.filter((x) => x.userId !== userId));
            setConfirmEvictUserOpen(null);
        },
        onError: (error: any) => {
            showNotification(`Evict user sessions failed: ${error?.message}`, 'error');
            setConfirmEvictUserOpen(null);
        },
    });

    const evictAllMutation = useMutation({
        mutationFn: () => agentService.evictAllConversationSessions(),
        onSuccess: (data) => {
            showNotification(`${data.sessionsEvicted} sessions evicted`, 'success');
            setSessions([]);
            setConfirmEvictAllOpen(false);
        },
        onError: (error: any) => {
            showNotification(`Evict all failed: ${error?.message}`, 'error');
            setConfirmEvictAllOpen(false);
        },
    });

    const viewSession = async (s: ConversationSessionEntry) => {
        try {
            const detail = await agentService.getConversationSession(s.userId, s.agentId, s.sessionId);
            setSelectedSession(s);
            setSessionMessages(detail.messages ?? []);
            setDrawerOpen(true);
        } catch (error: any) {
            showNotification(`Failed to load session: ${error?.message}`, 'error');
        }
    };

    const formatTtl = (seconds: number) => {
        if (seconds < 0) return 'No expiry';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p style={{ margin: 0, color: 'var(--mds-color-neutral-text-secondary)', fontSize: '13px' }}>
                    Per-user, per-agent chat sessions stored in Redis with auto-expiry TTL. Sessions are created automatically when users interact with agents and expire after the configured timeout.
                </p>

                {/* ── Inactivity Settings ── */}
                <McCard style={{ padding: '0', overflow: 'hidden' }}>
                    <button
                        onClick={handleToggleSettings}
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '14px 20px',
                            border: 'none',
                            background: 'var(--mds-color-neutral-weak-canvas)',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <McIcon icon="wrench" />
                            Inactivity &amp; Memory Settings
                            {settings && (
                                <span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--mds-color-neutral-text-secondary)' }}>
                                    — Timeout: {formatTtlInput(settings.ttlSeconds)}, Max Turns: {settings.maxTurns}
                                </span>
                            )}
                        </span>
                        <McIcon icon={settingsExpanded ? 'chevron-down' : 'chevron-right'} />
                    </button>
                    {settingsExpanded && (
                        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', borderTop: '1px solid var(--mds-color-neutral-weak-decoration)' }}>
                            {settingsQuery.isPending ? (
                                <div style={{ textAlign: 'center', padding: '12px', color: 'var(--mds-color-neutral-text-secondary)' }}>Loading settings...</div>
                            ) : (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--mds-color-neutral-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            </label>
                                            <McInput
                                                label='Inactivity Timeout (seconds)'
                                                type="number"
                                                value={editTtl}
                                                onInput={(e: any) => setEditTtl(e.target.value)}
                                                placeholder="7200"
                                                style={{ width: '100%' } as any}
                                            />
                                            <span style={{ fontSize: '11px', color: 'var(--mds-color-neutral-text-secondary)', marginTop: '4px', display: 'block' }}>
                                                Sessions auto-clear after this many seconds of inactivity. Min: 60s. Current: {editTtl ? formatTtlInput(parseInt(editTtl, 10) || 0) : '—'}
                                            </span>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--mds-color-neutral-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            </label>
                                            <McInput
                                                label='Max Turns Per Session'
                                                type="number"
                                                value={editMaxTurns}
                                                onInput={(e: any) => setEditMaxTurns(e.target.value)}
                                                placeholder="20"
                                                style={{ width: '100%' } as any}
                                            />
                                            <span style={{ fontSize: '11px', color: 'var(--mds-color-neutral-text-secondary)', marginTop: '4px', display: 'block' }}>
                                                Sliding window size — oldest turns are discarded when limit is reached. Min: 2.
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                        <McButton
                                            appearance="secondary"
                                            variant="outlined"
                                            onClick={() => { if (settings) { setEditTtl(String(settings.ttlSeconds)); setEditMaxTurns(String(settings.maxTurns)); } }}
                                        >
                                            Reset
                                        </McButton>
                                        <McButton
                                            appearance="primary"
                                            variant="filled"
                                            onClick={handleSaveSettings}
                                            disabled={saveSettingsMutation.isPending}
                                        >
                                            {saveSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                                        </McButton>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </McCard>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <McInput
                        type="search"
                        placeholder="Filter by user ID..."
                        value={userFilter}
                        onInput={(e: any) => setUserFilter(e.target.value)}
                        style={{ flex: 1 } as any}
                    />
                    <McButton
                        appearance="primary"
                        variant="outlined"
                        onClick={() => loadMutation.mutate({ user: userFilter, limit: 200 })}
                        disabled={loadMutation.isPending}
                    >
                        {loadMutation.isPending ? 'Loading...' : 'Search'}
                    </McButton>
                    <McButton
                        appearance="secondary"
                        variant="outlined"
                        onClick={() => loadMutation.mutate({ user: userFilter, limit: 200 })}
                        disabled={loadMutation.isPending}
                    >
                        <McIcon icon="rotate" />
                    </McButton>
                    <McButton
                        appearance="error"
                        variant="outlined"
                        onClick={() => setConfirmEvictAllOpen(true)}
                        disabled={sessions.length === 0}
                    >
                        <McIcon icon="trash" slot="prefix" />
                        Evict All Sessions
                    </McButton>
                </div>

                {!loaded && !loadMutation.isPending ? (
                    <McCard style={{ padding: '48px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                        <h3>Load Conversation Sessions</h3>
                        <p style={{ color: 'var(--mds-color-neutral-text-secondary)', maxWidth: '500px' }}>
                            Click Search to fetch active conversation sessions from Redis. You can filter by user ID or leave empty to see all sessions.
                        </p>
                        <McButton
                            appearance="primary"
                            onClick={() => loadMutation.mutate({ user: '', limit: 200 })}
                            style={{ marginTop: '16px' }}
                        >
                            Load Sessions
                        </McButton>
                    </McCard>
                ) : sessions.length === 0 && !loadMutation.isPending ? (
                    <McCard style={{ padding: '24px', textAlign: 'center', color: 'var(--mds-color-neutral-text-secondary)' }}>
                        No active conversation sessions found.
                    </McCard>
                ) : (
                    <McCard style={{ padding: '0', overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: 'var(--mds-color-neutral-weak-canvas)' }}>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid var(--mds-color-neutral-weak-decoration)', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--mds-color-neutral-text-secondary)' }}>User ID</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid var(--mds-color-neutral-weak-decoration)', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--mds-color-neutral-text-secondary)' }}>Agent ID</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '2px solid var(--mds-color-neutral-weak-decoration)', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--mds-color-neutral-text-secondary)' }}>Messages</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '2px solid var(--mds-color-neutral-weak-decoration)', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--mds-color-neutral-text-secondary)' }}>TTL</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '2px solid var(--mds-color-neutral-weak-decoration)', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--mds-color-neutral-text-secondary)' }}>Session ID</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '2px solid var(--mds-color-neutral-weak-decoration)', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--mds-color-neutral-text-secondary)' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessions.map((s) => (
                                        <tr key={s.key} style={{ borderBottom: '1px solid var(--mds-color-neutral-weak-decoration)' }}>
                                            <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '12px' }}>{s.userId}</td>
                                            <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '11px' }}>{s.agentId}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                <span style={{
                                                    backgroundColor: 'var(--mds-color-primary-weak-canvas, #e8f0fe)',
                                                    color: 'var(--mds-color-primary-60, #0073ab)',
                                                    padding: '2px 10px',
                                                    borderRadius: '12px',
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                }}>{s.messageCount}</span>
                                            </td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px' }}>{formatTtl(s.ttlSeconds)}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center', fontFamily: 'monospace', fontSize: '10px', color: 'var(--mds-color-neutral-text-secondary)' }}>{s.sessionId.substring(0, 8)}...</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                                    <McButton appearance="secondary" variant="plain" fit="small" onClick={() => viewSession(s)} title="View messages">
                                                        <McIcon icon="eye" />
                                                    </McButton>
                                                    <McButton appearance="error" variant="plain" fit="small" onClick={() => evictOneMutation.mutate(s)} title="Evict session">
                                                        <McIcon icon="trash" />
                                                    </McButton>
                                                    <McButton appearance="error" variant="plain" fit="small" onClick={() => setConfirmEvictUserOpen(s.userId)} title="Evict all sessions for this user">
                                                        <McIcon icon="users" />
                                                    </McButton>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ padding: '12px 16px', backgroundColor: 'var(--mds-color-neutral-weak-canvas)', borderTop: '1px solid var(--mds-color-neutral-weak-decoration)', fontSize: '12px', color: 'var(--mds-color-neutral-text-secondary)' }}>
                            Showing {sessions.length} session{sessions.length !== 1 ? 's' : ''}
                        </div>
                    </McCard>
                )}
            </div>

            <ConfirmationDialog
                open={confirmEvictAllOpen}
                onClose={() => setConfirmEvictAllOpen(false)}
                onConfirm={() => evictAllMutation.mutate()}
                title="Evict All Conversation Sessions"
                content={
                    <div>
                        <p>Are you sure you want to evict <strong>all</strong> conversation sessions?</p>
                        <p style={{ color: 'var(--mds-color-error-60)', marginTop: '8px' }}>
                            This will delete all in-flight chat context for every user.
                        </p>
                    </div>
                }
                confirmLabel="Evict All Sessions"
                variant="danger"
                loading={evictAllMutation.isPending}
            />

            <ConfirmationDialog
                open={!!confirmEvictUserOpen}
                onClose={() => setConfirmEvictUserOpen(null)}
                onConfirm={() => confirmEvictUserOpen && evictUserMutation.mutate(confirmEvictUserOpen)}
                title="Evict User Sessions"
                content={
                    <div>
                        <p>Evict all conversation sessions for user <strong>{confirmEvictUserOpen}</strong>?</p>
                    </div>
                }
                confirmLabel="Evict User Sessions"
                variant="danger"
                loading={evictUserMutation.isPending}
            />

            {drawerOpen && selectedSession && (
                <McDrawer
                    open={drawerOpen}
                    closed={() => { setDrawerOpen(false); setSelectedSession(null); setSessionMessages([]); }}
                    heading={`Session: ${selectedSession.userId}`}
                    width="600px"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: 'var(--mds-color-neutral-text-secondary)' }}>
                            <div><strong>Agent:</strong> {selectedSession.agentId}</div>
                            <div><strong>Session:</strong> {selectedSession.sessionId}</div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {sessionMessages.length === 0 ? (
                                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--mds-color-neutral-text-secondary)' }}>No messages in this session.</div>
                            ) : sessionMessages.map((msg, i) => (
                                <div
                                    key={i}
                                    style={{
                                        padding: '10px 14px',
                                        borderRadius: '8px',
                                        backgroundColor: msg.role === 'user'
                                            ? 'var(--mds-color-primary-weak-canvas, #e8f0fe)'
                                            : 'var(--mds-color-neutral-weak-canvas, #f5f5f5)',
                                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                        maxWidth: '90%',
                                        fontSize: '13px',
                                    }}
                                >
                                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--mds-color-neutral-text-secondary)', marginBottom: '4px', textTransform: 'uppercase' }}>
                                        {msg.role}
                                    </div>
                                    {msg.content.split(/(```[\s\S]*?```)/g).map((part, pi) => {
                                        const codeMatch = part.match(/^```(\w*)\n?([\s\S]*?)```$/);
                                        if (codeMatch) {
                                            return (
                                                <pre key={pi} style={{
                                                    margin: '6px 0 0',
                                                    padding: '10px 12px',
                                                    borderRadius: '4px',
                                                    backgroundColor: 'var(--mds-color-neutral-strong-canvas, #1e1e1e)',
                                                    color: 'var(--mds-color-neutral-white, #fff)',
                                                    fontSize: '12px',
                                                    lineHeight: 1.5,
                                                    overflowX: 'auto',
                                                    whiteSpace: 'pre',
                                                    wordBreak: 'normal',
                                                    fontFamily: 'monospace',
                                                    maxWidth: '100%',
                                                }}>{codeMatch[2]}</pre>
                                            );
                                        }
                                        return part ? (
                                            <span key={pi} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{part}</span>
                                        ) : null;
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </McDrawer>
            )}
        </>
    );
}

function CacheClusterCard({ cacheName, podStats, onViewKeys, onRefresh }: { cacheName: string; podStats: CacheStatistics[]; onViewKeys: (name: string) => void; onRefresh: () => void }) {
    const { showNotification } = useNotification();
    const [confirmEvictOpen, setConfirmEvictOpen] = useState(false);

    const evictMutation = useMutation({
        mutationFn: () => evictCache(cacheName),
        onSuccess: () => {
            showNotification(`Cache '${cacheName}' evicted successfully`, 'success');
            onRefresh();
            setConfirmEvictOpen(false);
        },
        onError: (error: any) => {
            showNotification(`Failed to evict cache: ${error.message}`, 'error');
            setConfirmEvictOpen(false);
        }
    });

    // Aggregate stats
    const totalHits = podStats.reduce((sum, p) => sum + (p.hits || 0), 0);
    const totalMisses = podStats.reduce((sum, p) => sum + (p.misses || 0), 0);
    const redisStat = podStats.find(p => p.source === 'REDIS') || podStats[0]; // Memory/Keys is global for Redis
    const totalMemory = redisStat?.totalMemoryUsage || 0;
    const memoryDisplay = redisStat?.formattedMemoryUsage || (totalMemory > 0 ? `${(totalMemory / 1024).toFixed(2)} KB` : '0 B');
    const keyCount = redisStat?.keyCount || 0;

    return (
        <McCard style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, wordBreak: 'break-all' }}>{cacheName}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <span style={{
                        backgroundColor: redisStat?.source === 'REDIS' ? 'var(--mds-color-primary-weak-decoration)' : 'var(--mds-color-neutral-weak-decoration)',
                        color: redisStat?.source === 'REDIS' ? 'var(--mds-color-primary-60)' : 'var(--mds-color-neutral-60)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}>
                        {redisStat?.source || 'UNKNOWN'}
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--mds-color-neutral-text-secondary)' }}>
                        Pods reporting: {podStats.length}
                    </span>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <StatBox label="Keys" value={keyCount.toLocaleString()} />
                <StatBox label="Memory" value={memoryDisplay} />
                <StatBox label="Total Hits" value={totalHits.toLocaleString()} />
                <StatBox label="Total Misses" value={totalMisses.toLocaleString()} />
            </div>

            <details style={{ fontSize: '12px', marginTop: '8px' }}>
                <summary style={{ cursor: 'pointer', color: 'var(--mds-color-primary-60)', fontWeight: 'bold' }}>Instance Breakdown</summary>
                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: 'var(--mds-color-neutral-weak-canvas)', borderRadius: '4px' }}>
                    <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ borderBottom: '1px solid var(--mds-color-neutral-weak-decoration)', paddingBottom: '4px' }}>Pod ID</th>
                                <th style={{ borderBottom: '1px solid var(--mds-color-neutral-weak-decoration)', paddingBottom: '4px' }}>Hits</th>
                                <th style={{ borderBottom: '1px solid var(--mds-color-neutral-weak-decoration)', paddingBottom: '4px' }}>Misses</th>
                            </tr>
                        </thead>
                        <tbody>
                            {podStats.map((pod, i) => (
                                <tr key={i}>
                                    <td style={{ paddingTop: '4px', fontFamily: 'monospace' }}>{pod.instanceId}</td>
                                    <td style={{ paddingTop: '4px' }}>{pod.hits}</td>
                                    <td style={{ paddingTop: '4px' }}>{pod.misses}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>

            <div style={{ display: 'flex', gap: '12px', marginTop: 'auto' }}>
                <McButton
                    appearance="secondary"
                    variant="outlined"
                    style={{ flex: 1 }}
                    disabled={keyCount === 0}
                    onClick={() => onViewKeys(cacheName)}
                >
                    View Keys
                </McButton>
                <McButton
                    appearance="error"
                    variant="outlined"
                    style={{ flex: 1 }}
                    onClick={() => setConfirmEvictOpen(true)}
                    disabled={evictMutation.isPending || keyCount === 0}
                >
                    Evict
                </McButton>
            </div>

            <ConfirmationDialog
                open={confirmEvictOpen}
                onClose={() => setConfirmEvictOpen(false)}
                onConfirm={() => evictMutation.mutate()}
                title={`Evict Cache: ${cacheName}`}
                content={
                    <div>
                        <p>Are you sure you want to evict the <strong>{cacheName}</strong> cache?</p>
                        <p style={{ marginTop: '8px' }}>
                            This action will clear all entries in this cache region across all pods.
                        </p>
                    </div>
                }
                confirmLabel="Evict"
                variant="danger"
                loading={evictMutation.isPending}
            />
        </McCard>
    );
}

function StatBox({ label, value }: { label: string; value: string | number }) {
    return (
        <div style={{
            backgroundColor: 'var(--mds-color-neutral-weak-canvas)',
            padding: '12px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
        }}>
            <span style={{ fontSize: '12px', color: 'var(--mds-color-neutral-text-secondary)', textTransform: 'uppercase' }}>
                {label}
            </span>
            <strong style={{ fontSize: '18px' }}>{value}</strong>
        </div>
    );
}

function CacheKeysDrawer({ cacheName, isOpen, onClose }: { cacheName: string; isOpen: boolean; onClose: () => void }) {
    const queryClient = useQueryClient();
    const { showNotification } = useNotification();
    const [search, setSearch] = useState('');

    const { data: keys, isLoading } = useQuery({
        queryKey: ['cacheKeys', cacheName],
        queryFn: () => getCacheKeys(cacheName),
        enabled: isOpen,
    });

    const evictKeyMutation = useMutation({
        mutationFn: (key: string) => evictCacheKey(cacheName, key),
        onSuccess: (_, evictedKey) => {
            showNotification(`Key evicted successfully`, 'success');
            queryClient.setQueryData(['cacheKeys', cacheName], (oldKeys: string[] | undefined) => {
                return oldKeys?.filter(k => k !== evictedKey) || [];
            });
        },
        onError: (error: any) => {
            showNotification(`Failed to evict key: ${error.message}`, 'error');
        }
    });

    const filteredKeys = keys?.filter(k => k.toLowerCase().includes(search.toLowerCase())) || [];

    return (
        <McDrawer
            open={isOpen}
            closed={onClose}
            heading={`Keys: ${cacheName}`}
            width="600px"
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '16px' }}>
                <p style={{ margin: 0, color: 'var(--mds-color-neutral-text-secondary)' }}>
                    Total keys: {keys?.length || 0}
                </p>

                <McInput
                    type="search"
                    placeholder="Search keys..."
                    value={search}
                    onInput={(e: any) => setSearch(e.target.value)}
                    style={{ width: '100%' } as any}
                />

                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {isLoading ? (
                        <div style={{ padding: '24px', textAlign: 'center' }}>Loading keys...</div>
                    ) : filteredKeys.length === 0 ? (
                        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--mds-color-neutral-text-secondary)' }}>
                            No keys found.
                        </div>
                    ) : (
                        <McList>
                            {filteredKeys.map(key => (
                                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--mds-color-neutral-weak-decoration)', padding: '8px 0' }}>
                                    <div style={{
                                        fontFamily: 'monospace',
                                        fontSize: '13px',
                                        wordBreak: 'break-all',
                                        paddingRight: '16px'
                                    }}>
                                        {key}
                                    </div>
                                    <McButton
                                        appearance="error"
                                        variant="plain"
                                        fit="small"
                                        onClick={() => evictKeyMutation.mutate(key)}
                                        disabled={evictKeyMutation.isPending}
                                    >
                                        <McIcon icon="trash" />
                                    </McButton>
                                </div>
                            ))}
                        </McList>
                    )}
                </div>
            </div>
        </McDrawer>
    );
}
